import { useNavigate } from "react-router-dom";
import ModalComponent from "..";
import {
  useCheckGuestInMutation,
  useGetEventGuestQuery,
} from "../../../redux/features/event/event.api";
import { FEE_PERCENTAGE_TEXT, PRIMARY_COLOR } from "../../../utils/constants";
import {
  calcTotalTicketPrice,
  formatDate,
  formatToNaira,
} from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { GuestCard } from "../../cards/guestCard";
import { ProfileIcon } from "../../cards/profilePic";
import { EmptyState } from "../../empty";
import { Icon } from "../../icon/icon";
import { LoadingOval } from "../../loaders/loaders";
import { Pill } from "../../pill";
import { TableComponent } from "../../table";
import { MiniHeader } from "../../typography/miniHeader";

export const ViewGuestModal = ({
  open,
  handleClose,
  guestId,
  adminView = false,
}: ModalCompProps & { guestId: string; adminView?: boolean }) => {
  const {
    data: guest,
    isFetching: fetchingGuestDetails,
    refetch,
    isSuccess: guestDetailsSuccess,
  } = useGetEventGuestQuery(
    {
      id: guestId,
    },
    { refetchOnMountOrArgChange: true, skip: guestId === "" },
  );

  const [handleGuestCheckIn, { isLoading: checkingGuestIn }] =
    useCheckGuestInMutation();

  const navigate = useNavigate();

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      containerClassName="md:!py-[20px] md:!px-[20px] md:!justify-end"
      className="!max-h-[90%] !rounded-t-[16px] !bg-white !p-[20px] md:!max-h-[100%] md:!max-w-[500px] md:!rounded-[16px]"
      dialogClassName="!my-0"
      showHeader
      mobileAnchorBottom
      closeOnOverlayClick
      fullScreen
      showCloseIcon
      showMobileTopBar={false}
    >
      {fetchingGuestDetails ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingOval color={PRIMARY_COLOR} />
        </div>
      ) : (
        <>
          {guestDetailsSuccess ? (
            <div className="flex w-full flex-col gap-8">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <ProfileIcon dimension={64} img="" className="!p-[12px]" />

                <div>
                  <p className="text-base font-bold tracking-tight text-grey-900 md:text-2xl">
                    {guest?.data?.firstName} {guest?.data?.lastName}
                  </p>
                  <p className="text-sm font-bold text-grey-500 md:text-base">
                    {guest?.data?.email}
                  </p>
                </div>

                {guest?.data?.status === "checked-in" ? (
                  <p className="text-grey-500">
                    Checked in on{" "}
                    {formatDate(
                      guest?.data?.updatedAt,
                      "DD MMM, YYYY • hh:mm A",
                    )}
                  </p>
                ) : undefined}
                {!adminView && (
                  <>
                    {guest?.data?.status === "unchecked-in" ? (
                      <Button
                        type="button"
                        onClick={async () => {
                          const resp = await handleGuestCheckIn({
                            id: guestId,
                          });

                          if (resp.data?.status === "success") {
                            refetch();
                          }
                        }}
                        text="Check In Guests"
                        icon="calendar-check-2"
                        variation="plain-grey"
                        className="!max-w-[272px]"
                        disabled={checkingGuestIn}
                        loading={checkingGuestIn}
                      />
                    ) : (
                      <Button
                        type="button"
                        onClick={() => {}}
                        text="Checked In"
                        icon="circle-check"
                        variation="green-outline"
                        className="!max-w-[272px] hover:!scale-100"
                      />
                    )}
                  </>
                )}

                {adminView && (
                  <Button
                    type="button"
                    onClick={() => {
                      navigate(`/users/${guest?.data?.firstName}`);
                    }}
                    text="View User"
                    variation="plain-grey"
                    className="!max-w-[272px] hover:!scale-100"
                  />
                )}
              </div>

              <TableComponent
                headers={[]}
                rows={[
                  {
                    id: 1,
                    items: [
                      <div className="flex flex-col gap-[2px]">
                        <div className="flex items-center justify-between gap-2 font-bold text-primary">
                          <p>
                            {guest?.data?.tickets?.reduce((acc, el) => {
                              return acc + el.quantity;
                            }, 0)}{" "}
                            {guest?.data?.tickets?.reduce((acc, el) => {
                              return acc + el.quantity;
                            }, 0) === 1
                              ? "Ticket"
                              : "Tickets"}
                          </p>

                          <p className="text-base font-bold">
                            {formatToNaira(
                              calcTotalTicketPrice(
                                guest?.data?.tickets?.reduce((acc, el) => {
                                  return (
                                    acc +
                                    (el?.ticketId?.price ?? 0) * el.quantity
                                  );
                                }, 0) ?? 0,
                                guest?.data?.discount?.value,
                                guest?.data?.discount?.type,
                                false,
                              ),
                            )}
                          </p>
                        </div>

                        <div className="flex items-start gap-2">
                          <div className="flex flex-1 flex-wrap items-center gap-2">
                            {guest?.data?.tickets?.map((t, idx) => {
                              return (
                                <Pill
                                  text={`${t.quantity}X ${t?.ticketId?.name}`}
                                  variation="grey"
                                  key={`${idx}-ticket`}
                                />
                              );
                            })}
                          </div>

                          {guest?.data?.discount && (
                            <p className="text-base text-grey-500 line-through">
                              {formatToNaira(
                                calcTotalTicketPrice(
                                  guest?.data?.tickets?.reduce((acc, el) => {
                                    return (
                                      acc +
                                      (el?.ticketId?.price ?? 0) * el.quantity
                                    );
                                  }, 0) ?? 0,
                                  undefined,
                                  undefined,
                                  false,
                                ),
                              )}
                            </p>
                          )}
                        </div>
                      </div>,
                    ],
                  },
                  {
                    id: 2,
                    items: [
                      guest?.data?.discount ? (
                        <div className="flex items-center gap-2">
                          <Icon
                            svg="ticket-percent"
                            width={24}
                            height={24}
                            className="text-blue"
                          />

                          <p className="flex-1 text-base font-bold uppercase text-primary">
                            {guest?.data?.discount?.name}
                          </p>

                          <p className="text-base text-grey-500">
                            {guest?.data?.discount?.type === "amount"
                              ? `${formatToNaira(guest?.data?.discount?.value)} off`
                              : ""}
                            {guest?.data?.discount?.type === "percentage"
                              ? `${guest?.data?.discount?.value}% off`
                              : ""}
                          </p>
                        </div>
                      ) : undefined,
                    ],
                  },
                  {
                    id: 3,
                    items: [
                      <div className="flex flex-col gap-[2px]">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-grey-500">Our Fee</p>

                          <Pill
                            text={`${FEE_PERCENTAGE_TEXT} FEE`}
                            variation="grey"
                          />
                        </div>

                        <p className="font-bold text-primary">
                          {formatToNaira(guest?.data?.payment?.fees ?? 0)}
                        </p>
                      </div>,
                    ],
                  },
                  {
                    id: 4,
                    items: [
                      <div>
                        <p className="text-base text-grey-500">
                          Date Registered
                        </p>
                        <p className="text-base font-bold text-primary">
                          {formatDate(
                            guest?.data.createdAt,
                            "MMMM DD, YYYY • hh:mm A",
                          )}
                        </p>
                      </div>,
                    ],
                  },
                ]}
                onRowClick={(idx) => {}}
                isRowClickable={false}
                isTableEmpty={false}
              />

              {guest !== undefined &&
              ![0, undefined].includes(guest?.data?.invitedGuests?.length) ? (
                <div className="flex flex-col gap-2">
                  <MiniHeader header="Additional Guests" />

                  <TableComponent
                    headers={[]}
                    rows={guest.data.invitedGuests.map((invitedGuest, idx) => {
                      return {
                        id: idx,
                        items: [
                          <GuestCard
                            className=""
                            name={`${invitedGuest.firstName} ${invitedGuest.lastName}`}
                            email={`${invitedGuest.email}`}
                            timeAgo={undefined}
                            showTicket={false}
                            key={`invited-guests-row-${idx}`}
                          />,
                        ],
                      };
                    })}
                    onRowClick={(idx) => {}}
                    isRowClickable={false}
                    isTableEmpty={false}
                  />
                </div>
              ) : undefined}
            </div>
          ) : (
            <EmptyState
              text="Guest not found"
              image="/assets/images/guest-glass.png"
            />
          )}
        </>
      )}
    </ModalComponent>
  );
};
